import { createAsyncThunk } from '@reduxjs/toolkit';

import { selectIsAstSignMailDestinationClaimed } from './astSignMailSelectors';
import {
  claimAstSignMailHandoff,
  endAstSignMailHandoff,
  startAstSignMailHandoff,
  transferAstRelayResponseReceived,
} from './astSignMailSlice';
import {
  removeCurrentAgentFromVoiceSession,
  type AstSignMailAgentRemovalStatus,
} from './astSignMailHandoffHelpers';

import type { RootState } from 'features/app/store/store';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import {
  transferCallOwnershipToAST,
  transferCallToAST,
} from 'features/ast-signmail/helpers';
import type { TransferAstRelayResponse } from 'features/call/call-base/interfaces';
import {
  callIdSelector,
  handleLocalCallExit,
} from 'features/call/call-base/store';
import { setDeafStatus } from 'features/call/call-deaf/store';
import { missedCallAddNotificationThunk } from 'features/call/vrs-call/store';
import { handleError } from 'features/notification/store';
import { voiceSessionIdSelector } from 'features/voice-session/store';
import { agentNumberSelector } from 'features/user/store';

export const startAutoAstSignMailHandoff = createAsyncThunk(
  'astSignMail/startAutoAstSignMailHandoff',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    if (selectIsAstSignMailDestinationClaimed(state)) {
      dispatch(
        sendAnalyticsInfo({
          Method: 'startAutoAstSignMailHandoff',
          message:
            'AST SignMail handoff skipped because destination is already claimed.',
          LogGroup: 'SignMail',
        })
      );
      return;
    }

    const callId = callIdSelector(state);

    // Claim before ownership await so local SignMail cannot start mid-transfer.
    dispatch(claimAstSignMailHandoff());

    let transferAstRelayResponse: TransferAstRelayResponse;

    try {
      transferAstRelayResponse = await transferCallOwnershipToAST(callId);
    } catch (error) {
      dispatch(endAstSignMailHandoff());
      dispatch(
        handleError({
          error,
          methodName: 'transferCallOwnershipToAST',
          disableUserNotification: true,
        })
      );
      throw error;
    }

    dispatch(transferAstRelayResponseReceived(transferAstRelayResponse));
    dispatch(startAstSignMailHandoff());
    dispatch(setDeafStatus('unavailable'));
    await dispatch(missedCallAddNotificationThunk());
    dispatch(
      sendAnalyticsInfo({
        Method: 'startAutoSignMailFlow',
        message: 'AST SignMail handoff started for auto-triggered SignMail.',
        LogGroup: 'SignMail',
      })
    );

    try {
      await transferCallToAST(transferAstRelayResponse);
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'transferCallToAST',
        })
      );
    }
  }
);

export const completeAstSignMailHandoff = createAsyncThunk(
  'astSignMail/completeAstSignMailHandoff',
  async (_, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const voiceSessionId = voiceSessionIdSelector(state);
      const agentNumber = agentNumberSelector(state);
      const removalStatus: AstSignMailAgentRemovalStatus =
        await removeCurrentAgentFromVoiceSession({
          voiceSessionId,
          agentNumber,
          dispatch,
        });

      await dispatch(handleLocalCallExit()).unwrap();
      dispatch(endAstSignMailHandoff());

      dispatch(
        sendAnalyticsInfo({
          Method: 'completeAstSignMailHandoff',
          message: `AST SignMail handoff completed with ${removalStatus} local exit`,
          LogGroup: 'AstSignMail',
        })
      );
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'completeAstSignMailHandoff',
        })
      );
      throw error;
    }
  }
);
